/**
 * useAvailableInvoiceStatuses Hook
 * Fetches available invoice statuses from API based on current filters
 * Only shows statuses that have at least one record in the dataset
 *
 * @author SAUBHAGYA Development Team
 */

import { useState, useEffect } from 'react';
import type { InvoiceStatus } from '@/types/invoice';

export interface UseAvailableInvoiceStatusesReturn {
  availableStatuses: InvoiceStatus[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch available invoice statuses from the API
 * This ensures the filter only shows statuses that actually exist in the data
 */
export function useAvailableInvoiceStatuses(
  searchTerm?: string,
  dateFrom?: string,
  dateTo?: string
): UseAvailableInvoiceStatusesReturn {
  const [availableStatuses, setAvailableStatuses] = useState<InvoiceStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAvailableStatuses = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Build query parameters
        const params = new URLSearchParams();

        if (searchTerm) {
          params.append('searchTerm', searchTerm);
        }
        if (dateFrom) {
          params.append('dateFrom', dateFrom);
        }
        if (dateTo) {
          params.append('dateTo', dateTo);
        }

        // Fetch statuses from API
        // This endpoint should return distinct statuses from invoices matching the filters
        const baseURL = 'http://localhost:8083/commerce-service';
        const token = localStorage.getItem('saubhagya_jwt_token');
        const response = await fetch(
          `${baseURL}/api/v1/invoices/available-statuses?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token ? `Bearer ${token}` : '',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch available invoice statuses: ${response.statusText}`);
        }

        const responseData = await response.json();
        const statuses: InvoiceStatus[] = responseData.data || [];

        // Sort statuses in order of the workflow
        const statusOrder: InvoiceStatus[] = [
          'DRAFT',
          'ISSUED',
          'SENT',
          'PARTIALLY_PAID',
          'PAID',
          'OVERDUE',
          'CANCELLED',
          'VOID',
        ];

        const sortedStatuses = statuses.sort(
          (a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b)
        );

        setAvailableStatuses(sortedStatuses);
      } catch (err) {
        console.error('[useAvailableInvoiceStatuses] Error fetching available statuses:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch available statuses');
        // Fallback to empty array on error
        setAvailableStatuses([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailableStatuses();
  }, [searchTerm, dateFrom, dateTo]);

  return { availableStatuses, isLoading, error };
}

export default useAvailableInvoiceStatuses;
